import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MyTokens from './MyTokens';
import Tabs from './Tabs';
import HeaderV2 from '../../../components/HeaderV2';
import CollectionTable from './CollectionTable';
import Page from '../../Page';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { desktopMinWidth } from '../../../../utils/windowHelpers';
import colors from '../../../constants/colors';
import { Helmet } from 'react-helmet-async';
import Footer from '../../../components/Footer';
import { truncate } from '../../../../utils/string';
import LogoWithCircle from '../../../components/icons/LogoWithCircle';
import { getNftCollectionStats, getNfts } from '../actions';
import { defaultCollections } from '../constants';
import { useSnackbar } from 'notistack';
import { useConnectWallet } from 'app/contexts/ConnectWalletContext';

export default function TokenListContainer() {
  const useStyles = makeStyles(theme =>
    createStyles({
      root: {
        background: colors.darker_blue,
      },
      container: {
        margin: '64px 12px 80px 12px',
        position: 'relative',
        [desktopMinWidth]: {
          margin: '64px auto 84px auto',
        },
        '& a': {
          textDecoration: 'none',
        },
        fontFamily: 'Poppins',
      },
      modal: {
        background: colors.dark_blue,
        padding: '32px 12px',
        [desktopMinWidth]: {
          padding: '40px 24px',
        },
      },
      dialog: {
        position: 'relative',
        '& .MuiDialog-paper': {
          margin: '32px 16px !important',
          [desktopMinWidth]: {
            margin: '32px',
          },
        },
      },
      modalHeader: {
        color: colors.white_f2,
        fontWeight: 600,
        marginBottom: 24,
        fontSize: 20,
        [desktopMinWidth]: {
          fontSize: 24,
        },
      },
      logoContainer: {
        marginRight: 10,
        paddingTop: 6,
      },
      walletAddContainer: {
        margin: 'auto auto 32px auto',
        color: colors.white_f2,
        fontWeight: 400,
        fontSize: 18,
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
        [desktopMinWidth]: {
          marginBottom: 40,
          fontSize: 24,
        },
      },
      tabContent: {
        margin: '1rem 0 0.5rem',
      },
    }),
  );

  const classes = useStyles();

  const { walletAddress, onConnectClick } = useConnectWallet();
  const contentNotAccessible = useMemo(() => walletAddress === '', [
    walletAddress,
  ]);
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [collectionsWithStats, setCollectionsWithStats] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState(null);

  const tabs = useMemo(
    () => [
      {
        id: 1,
        title: 'My Tokens',
        content: (
          <MyTokens
            contentNotAccessible={contentNotAccessible}
            tokens={tokens}
            loading={loadingTokens}
          />
        ),
      },
      {
        id: 2,
        title: 'Collections',
        content: (
          <CollectionTable
            collections={collectionsWithStats}
            loading={loadingCollections}
          />
        ),
      },
    ],
    [
      contentNotAccessible,
      loadingTokens,
      loadingCollections,
      collectionsWithStats,
    ],
  );

  const getTokenList = useCallback(async walletAddress => {
    setLoadingTokens(true);
    try {
      const tokenRes = await getNfts({
        ownerAdd: walletAddress,
      });
      setTokens(tokenRes?.data?.ownedNfts);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.statusText
          ? error?.response?.statusText
          : 'Cannot retrieve tokens!',
        { variant: 'error', persist: false },
      );
    }
    setLoadingTokens(false);
  }, []);

  const getCollectionsWithStats = useCallback(async collections => {
    setLoadingCollections(true);
    try {
      await Promise.all(
        collections.map(async collection => {
          const collectionSlug = collection.collectionSlug;
          try {
            const res = await getNftCollectionStats({
              collectionSlug,
            });
            collection.stats = res?.data;
          } catch (e) {
            console.log(
              `Unable to retrieve collection ${collectionSlug} stats!`,
            );
          }
        }),
      );
    } catch (e) {
      console.log(`Cannot retrieve collections info ${e}`);
    }
    setCollectionsWithStats(collections);
    setLoadingCollections(false);
  }, []);

  // const addWalletListener = () => {
  //   //@ts-ignore
  //   if (window.ethereum) {
  //     //@ts-ignore
  //     window.ethereum.on('accountsChanged', accounts => {
  //       if (accounts.length > 0) {
  //         setWalletAddress(accounts[0]);
  //       } else {
  //         setWalletAddress('');
  //       }
  //     });
  //   }
  // };

  // const setInitialConnectedWallet = useCallback(async () => {
  //   const { address } = await getCurrentWalletConnected();

  //   setWalletAddress(address);
  // }, []);

  // useEffect(() => {
  //   setInitialConnectedWallet().then();
  //   addWalletListener();
  // }, []);

  useEffect(() => {
    if (!walletAddress) onConnectClick();
  }, [walletAddress]);

  useEffect(() => {
    if (!contentNotAccessible) {
      getTokenList(walletAddress).then();
    }
  }, [contentNotAccessible, walletAddress]);

  useEffect(() => {
    getCollectionsWithStats(defaultCollections).then();
  }, []);

  useEffect(() => {
    setCurrentTab(tabs[0]);
  }, [tabs]);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>NFT Tokens</title>
        <meta name="title" content="NFT Tokens" />
        <meta
          name="description"
          content="Empowering Education using Web3/Blockchain technology. We are the technology arm of Nas Academy, whose mission is to create the world's best learning communities."
        />
        <meta
          property="og:image"
          content="https://s3.ap-southeast-1.amazonaws.com/image-assets.nasdaily.com/lesan/nasio/nasio_cover.png"
        />
        <meta
          property="og:url"
          content={`${process.env.REACT_APP_FRONTEND_URL}/tokens`}
        />
      </Helmet>
      <HeaderV2
        showNavlinks={true}
        headerTheme="blue"
        walletAddress={walletAddress}
        showLogin={false}
      />
      <Page>
        <div className={classes.container}>
          <div className={classes.walletAddContainer}>
            <div className={classes.logoContainer}>
              <LogoWithCircle />
            </div>
            {!!walletAddress && truncate(walletAddress, 16, '')}
          </div>
          <Tabs
            tabs={tabs}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
          />
          <div className={classes.tabContent}>{currentTab?.content}</div>
        </div>
        <Footer blueTheme={true} />
      </Page>
    </div>
  );
}
